// A table document that is mid-creation or mid-deletion is not a table the user can use.
// create_table stamps `status: 'Creating'` while it reserves the name; delete_table stamps
// `status: 'Deleting'` with `deleting_claimed_at` before it removes BigQuery, storage and
// Firestore (M233). From that stamp on the table is never usable again — parts of it may
// already be gone — so `isTableUsable` is false for it in every state, and three states
// follow from the timestamp:
//
// - in progress: the timestamp is fresh. The Data page hides the table.
// - interrupted: the attempt failed in a handled way (the backend drops the timestamp and
//   records `deletion_error`) or crashed (the timestamp went stale). The Data page shows the
//   table with retrying the deletion as its only action; the retry takes the stamp over and
//   resumes, the same recovery create_table uses for a dead Creating reservation.
// - gone: the document was removed.
//
// A creation has the same two live states, judged by `reservation_claimed_at`:
//
// - being created: the reservation is fresh. The Data page lists the name as a row that
//   cannot be opened, so people can see why the name is taken.
// - creation interrupted: the reservation went stale (create_table died before its last
//   step). The Data page lists it with deleting it as the only action; creating a table of
//   the same name again also takes the reservation over.
const DELETING_STAMP_STALE_MS = 10 * 60 * 1000;
// The backend's CHECKPOINT_STALE_SECONDS: how old a reservation must be before create_table
// lets another creation take it over, and before delete_table agrees to remove it.
const CREATION_RESERVATION_STALE_MS = 10 * 60 * 1000;

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

// A deletion attempt is running right now: hidden from every list.
export function isTableBeingDeleted(table, now = Date.now()) {
  if (table?.status !== 'Deleting') return false;
  const claimedAt = toMillis(table.deleting_claimed_at);
  if (claimedAt === null) return false;
  return now - claimedAt < DELETING_STAMP_STALE_MS;
}

// A deletion began and did not finish: listed on the Data page for retry only.
export function isTableDeletionInterrupted(table, now = Date.now()) {
  return table?.status === 'Deleting' && !isTableBeingDeleted(table, now);
}

// create_table is running right now: listed, but not something the user can open.
export function isTableBeingCreated(table, now = Date.now()) {
  if (table?.status !== 'Creating') return false;
  const reservedAt = toMillis(table.reservation_claimed_at);
  // create_table always writes the timestamp with the status. The backend never treats a
  // reservation without one as stale (timestamp_is_stale(None) is false), so create_table
  // and delete_table both refuse it; offering its deletion here would offer a button that
  // always fails.
  if (reservedAt === null) return true;
  return now - reservedAt < CREATION_RESERVATION_STALE_MS;
}

// A creation began and did not finish: listed on the Data page to be deleted.
export function isTableCreationInterrupted(table, now = Date.now()) {
  return table?.status === 'Creating' && !isTableBeingCreated(table, now);
}

export function isTableUsable(table) {
  return table?.status !== 'Creating' && table?.status !== 'Deleting';
}

// How long until the earliest fresh stamp among `tables` goes stale — a Deleting stamp or a
// creation reservation — or null when none is fresh. What a list shows for such a table
// changes at that moment (a hidden table comes back as "Deletion interrupted", a "Creating…"
// row becomes "Creation interrupted"), and a dead attempt writes nothing more to wake the
// listener — so the list re-evaluates itself when the stamp expires.
export function msUntilATableStampExpires(tables, now = Date.now()) {
  let earliest = null;
  (tables || []).forEach((table) => {
    let expiresAt = null;
    if (isTableBeingDeleted(table, now)) {
      expiresAt = toMillis(table.deleting_claimed_at) + DELETING_STAMP_STALE_MS;
    } else if (isTableBeingCreated(table, now)) {
      const reservedAt = toMillis(table.reservation_claimed_at);
      // A reservation with no timestamp never goes stale, so there is nothing to wait for.
      if (reservedAt !== null) expiresAt = reservedAt + CREATION_RESERVATION_STALE_MS;
    }
    if (expiresAt === null) return;
    if (earliest === null || expiresAt < earliest) earliest = expiresAt;
  });
  return earliest === null ? null : Math.max(earliest - now, 0) + 1000;
}
